'use client';

import { useEnvContext } from 'next-runtime-env';
import type { PropsWithChildren } from 'react';
import {
  type StatsigOptions,
  StatsigProvider as StatsigAsyncProvider,
  StatsigSynchronousProvider,
  type StatsigUser,
} from 'statsig-react';

// import { getCookie } from '@/utils/cookie';

interface StatsigClientProviderProps extends PropsWithChildren {
  readonly user: StatsigUser;
  // readonly stableId?: string;
  readonly data?: Record<string, unknown>;
  readonly optionOverrides?: StatsigOptions;
}

export default function StatsigProvider({
  user,
  data,
  children,
  // stableId,
  optionOverrides,
}: StatsigClientProviderProps): JSX.Element {
  const { NEXT_PUBLIC_STATSIG_CLIENT_KEY, NEXT_PUBLIC_OURA_ENV } =
    useEnvContext();
  if (!NEXT_PUBLIC_STATSIG_CLIENT_KEY) {
    throw new Error(
      'Missing NEXT_PUBLIC_STATSIG_CLIENT_KEY environment variable.',
    );
  }

  if (!NEXT_PUBLIC_OURA_ENV) {
    throw new Error('Missing NEXT_PUBLIC_OURA_ENV environment variable.');
  }

  const stableId = user.customIDs?.['stableID'];

  const options: StatsigOptions = {
    disableAutoMetricsLogging: true,
    disableErrorLogging: true,
    environment: { tier: `${NEXT_PUBLIC_OURA_ENV}` },
    // Override the stable id using the id generated on the server with the initial request in middleware.ts
    // This ensures that the stable id is the same for both the server and client
    ...(stableId && { overrideStableID: stableId }),
    ...optionOverrides,
  };

  // No statsig values loaded on the server, so we need to wait for the SDK to initialize
  if (data == null) {
    return (
      <StatsigAsyncProvider
        sdkKey={NEXT_PUBLIC_STATSIG_CLIENT_KEY}
        user={user}
        waitForInitialization={true}
        options={options}
      >
        {children}
      </StatsigAsyncProvider>
    );
  }
  return (
    <StatsigSynchronousProvider
      sdkKey={NEXT_PUBLIC_STATSIG_CLIENT_KEY}
      user={user}
      initializeValues={data}
      options={options}
    >
      {children}
    </StatsigSynchronousProvider>
  );
}
